import { SOURCE_DEMO, SOURCE_PRE_ORDER, SOURCE_TMS } from '../recordSource';

/**
 * Znacznik pochodzenia pola na karcie zlecenia (makieta FTL p. Mariusza,
 * pkt 16: „każda liczba ma widoczne pochodzenie”; Etap 1, decyzja Bartka
 * 15.10.2026).
 *
 * Cztery rodzaje plus dane pokazowe:
 *
 * - `tms` — wartość z TMS (import; kartoteka i zlecenie po stronie klienta);
 * - `preOrder` — z prezlecenia (mail albo formularz w aplikacji), jeszcze nie
 *   z TMS;
 * - `operator` — wpisał albo zatwierdził człowiek w aplikacji (wariant,
 *   obsadzenie, zapis do VIA);
 * - `calc` — policzyła aplikacja (reguła, mapa HERE, statystyka) — to nie
 *   pomiar i nie dana z TMS;
 * - `demo` — zlecenie pokazowe.
 *
 * Pochodzenia nie zgadujemy: gdy z danych nie wynika, znacznika nie ma
 * (`null`), zamiast udawać, że wiemy.
 */
export type FieldOrigin = 'tms' | 'preOrder' | 'operator' | 'calc' | 'demo';

export const ORIGIN_APPEARANCE: Record<FieldOrigin, { label: string; hint: string }> = {
  tms: { label: 'TMS', hint: 'Wartość z TMS — przychodzi z importu, w aplikacji jej nie zmieniamy.' },
  preOrder: {
    label: 'prezlecenie',
    hint: 'Wartość z prezlecenia (z maila albo wpisana w formularzu) — zlecenia nie ma jeszcze w TMS.',
  },
  operator: { label: 'wpis operatora', hint: 'Wpisane albo zatwierdzone przez użytkownika w aplikacji.' },
  calc: {
    label: 'wyliczenie',
    hint: 'Policzone przez aplikację (reguła, mapa albo statystyka) — to nie dana z TMS ani pomiar.',
  },
  demo: { label: 'DEMO', hint: 'Zlecenie pokazowe z danymi testowymi — nie pochodzi z systemów Marathonu.' },
};

type Loose = Record<string, unknown>;

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

/** Pochodzenie danych nagłówka zlecenia — ze źródła rekordu (`sourceSystem`). */
export function recordOrigin(record: Loose | undefined): FieldOrigin | null {
  const source = record?.sourceSystem;
  if (source === SOURCE_TMS) return 'tms';
  if (source === SOURCE_PRE_ORDER) return 'preOrder';
  if (source === SOURCE_DEMO) return 'demo';
  return null;
}

/**
 * Źródło wpisu dziennika (`UserOfferChange.source`) jako pochodzenie wartości:
 * ręcznie i OneHub (przypisanie zatwierdzone przez człowieka) — operator,
 * import i VIA — TMS, reguła — wyliczenie. Nieznane źródło — `null`.
 */
export function changeSourceOrigin(source: unknown): FieldOrigin | null {
  switch (source) {
    case 'MANUAL':
    case 'EXTERNAL':
      return 'operator';
    case 'IMPORT':
    case 'VIA':
      return 'tms';
    case 'RULE':
      return 'calc';
    default:
      return null;
  }
}

/**
 * Pochodzenie bieżącej wartości pola z dziennika: źródło najnowszego wpisu
 * o tym polu. API oddaje dziennik od najnowszego, ale nie polegamy na tym —
 * bierzemy wpis z najpóźniejszą datą. Bez wpisu — `fallback`.
 */
export function lastChangeOrigin(
  record: Loose | undefined,
  field: string,
  fallback: FieldOrigin | null,
): FieldOrigin | null {
  const changes = Array.isArray(record?.changes) ? (record?.changes as Loose[]) : [];
  let newest: Loose | null = null;
  for (const c of changes) {
    if (c?.field !== field || typeof c.changedAt !== 'string') continue;
    if (!newest || String(c.changedAt) > String(newest.changedAt)) newest = c;
  }
  if (!newest) return fallback;
  return changeSourceOrigin(newest.source) ?? fallback;
}

/** Stan planowania: stempel z autorem = człowiek; inaczej z ostatniego wpisu. */
export function planningStatusOrigin(record: Loose | undefined): FieldOrigin | null {
  const by = record?.planningStatusBy as { username?: unknown } | null | undefined;
  if (by && text(by.username)) return 'operator';
  return lastChangeOrigin(record, 'planningStatus', recordOrigin(record));
}

/** Obsadzony pojazd: źródło ostatniego wpisu o obsadzeniu (TMS, aplikacja, OneHub). */
export function assignedCarOrigin(record: Loose | undefined): FieldOrigin | null {
  if (record?.assignedCarId === null || record?.assignedCarId === undefined) return null;
  return lastChangeOrigin(record, 'assignedCarId', recordOrigin(record));
}

const positive = (n: unknown): boolean => typeof n === 'number' && Number.isFinite(n) && n > 0;

/**
 * Waga: z punktów TMS, a bez nich — z prezlecenia (ta sama kolejność co
 * `bankRowWeightText`). Nic — bez znacznika.
 */
export function weightOrigin(record: Loose | undefined): FieldOrigin | null {
  const points = Array.isArray(record?.keyPoints) ? (record?.keyPoints as Loose[]) : [];
  const fromPoints = points.some((p) => p?.type === 'loading' && positive(p.loadingWeight));
  if (fromPoints) return recordOrigin(record) === 'demo' ? 'demo' : 'tms';
  const preOrderWeight = (record?.preOrder as Loose | undefined)?.weight;
  if (preOrderWeight !== null && preOrderWeight !== undefined && Number(preOrderWeight) > 0) return 'preOrder';
  return null;
}

/** Kontrahent z kartoteki (TMS) albo sama nazwa klienta z prezlecenia. */
export function clientOrigin(record: Loose | undefined): FieldOrigin | null {
  if (record?.contractor) return recordOrigin(record) === 'demo' ? 'demo' : 'tms';
  if (text((record?.preOrder as Loose | undefined)?.clientName)) return 'preOrder';
  return null;
}
